import type { UseMutationResult } from "react-query"

import type { AxiosError, AxiosResponse } from "axios"
import type { FormikErrors, FormikTouched } from "formik"
import { v4 as uuidv4 } from "uuid"

import { Operators } from "../../types/custom/projects"
import type { Rule } from "../../types/custom/rules"
import type { ConditionListFile } from "../../types/generated/api/ConditionListFile"
import type { Label } from "../../types/generated/api/Label"
import type { RulesetRuleList } from "../../types/generated/api/RulesetRuleList"
import type { ScoreCardRule } from "../../types/generated/api/ScoreCardRule"
import type { TagListRuleList } from "../../types/generated/api/TagListRuleList"
import { WorkflowInputFeature } from "../../types/generated/api/WorkflowInputFeature"
import { multiValuesOperators } from "../../utils/conditionHelpers"
import { PROHIBITED_LABELS_KEYWORDS } from "../../utils/constants"
import { isInputValueValid } from "../../utils/genericHelpers"
import { FILE_ID_MATCHER } from "../../utils/rulesetHelpers"
import type { ScorecardRule } from "../Scorecards/Interfaces"
import type { TagCardFormikValues, TagGroupConditions } from "../Taglist"
import type { ConditionPath, GroupConditions, RuleCardFormikValues, RuleGroupConditions } from "./interfaces"

export function isRulesetRule(value: TagListRuleList | RulesetRuleList | ScoreCardRule): value is RulesetRuleList {
  return "return_label" in value
}

export function isRulesetFormikCard(
  value: TagGroupConditions | RuleGroupConditions | ScorecardRule,
): value is RuleGroupConditions {
  return "label" in value && "label_id" in value
}

/**
 * Get condition from the formik values based on path object
 */
export function getCondition(props: {
  values: RuleCardFormikValues
  ruleIndex: number
  path: ConditionPath
}): Rule | undefined {
  const { values, ruleIndex, path } = props
  const card = values?.cards?.[ruleIndex]
  if (!card) return undefined

  const { groupsIdx, conditionIdx } = path

  // Base case: if no groups, it's a root level condition
  if (groupsIdx.length === 0) {
    return card.conditions?.[conditionIdx] as Rule
  }

  // Navigate through groups
  let currentGroup: GroupConditions | RuleGroupConditions | undefined = card
  for (const groupIndex of groupsIdx) {
    currentGroup = currentGroup?.groups?.[groupIndex]
    if (!currentGroup) return undefined
  }

  // Get the condition from the final group
  return currentGroup.conditions?.[conditionIdx] as Rule
}

/**
 * Get field path for formik based on path object
 */
export function getFieldPath(props: { field: string; ruleIndex: number; path: ConditionPath }): string {
  const { field, ruleIndex, path } = props
  const { groupsIdx, conditionIdx } = path

  if (groupsIdx.length === 0) {
    // Root level condition
    return `cards[${ruleIndex}].conditions[${conditionIdx}].${field}`
  }

  // Build nested group path
  const groupPath = groupsIdx
    .map((idx, level) => {
      return level === 0 ? `groups[${idx}]` : `.groups[${idx}]`
    })
    .join("")

  return `cards[${ruleIndex}].${groupPath}.conditions[${conditionIdx}].${field}`
}

/**
 * Get touched condition from formik touched state using path object
 */
export function getTouchedCondition(props: {
  touched: FormikTouched<RuleCardFormikValues>
  ruleIndex: number
  path: ConditionPath
}): Rule | undefined {
  const { touched, ruleIndex, path } = props
  const card = touched?.cards?.[ruleIndex]
  if (!card || typeof card === "boolean") return undefined

  const cardObj = card as never as RuleGroupConditions
  const { groupsIdx, conditionIdx } = path

  // Base case: if no groups, it's a root level condition
  if (groupsIdx.length === 0) {
    return cardObj.conditions?.[conditionIdx]
  }

  // Navigate through groups
  let currentGroup: GroupConditions | RuleGroupConditions | undefined = cardObj
  for (const groupIndex of groupsIdx) {
    currentGroup = currentGroup.groups?.[groupIndex]
    if (!currentGroup) return undefined
  }

  // Get the condition from the final group
  return currentGroup.conditions?.[conditionIdx]
}

/**
 * Get error condition from formik errors state using path object
 */
export function getErrorCondition(props: {
  errors: FormikErrors<RuleCardFormikValues>
  ruleIndex: number
  path: ConditionPath
}): Rule | undefined {
  const { errors, ruleIndex, path } = props
  const card = errors?.cards?.[ruleIndex]
  if (!card || typeof card === "string") return undefined

  const cardObj = card as never as RuleGroupConditions
  const { groupsIdx, conditionIdx } = path

  // Base case: if no groups, it's a root level condition
  if (groupsIdx.length === 0) {
    return cardObj.conditions?.[conditionIdx]
  }

  // Navigate through groups
  let currentGroup: GroupConditions | RuleGroupConditions | undefined = cardObj
  for (const groupIndex of groupsIdx) {
    currentGroup = currentGroup.groups?.[groupIndex]
    if (!currentGroup) return undefined
  }

  // Get the condition from the final group
  return currentGroup.conditions?.[conditionIdx]
}

/**
 * Get initial new feature type based on condition operator and type
 */
export function getInitialNewFeatureType(condition?: Rule): WorkflowInputFeature.type {
  if ([Operators.between, ...multiValuesOperators].includes(condition?.operator as string)) {
    return WorkflowInputFeature.type.LIST
  } else if (condition?.type === "true" || condition?.type === "false") {
    return WorkflowInputFeature.type.BOOLEAN
  } else if (condition?.type === "number") {
    return WorkflowInputFeature.type.NUMBER
  } else {
    return WorkflowInputFeature.type.TEXT
  }
}

/**
 * Get operator value from translation using operators enum
 */
export function getOpValuefromTranslation(props: {
  translation: string
  t: (key: string) => string
}): string | undefined {
  const { translation, t } = props
  for (const value of Object.values(Operators)) {
    if (t(`operators.${value as string}`) === translation) {
      return value as string
    }
  }
}

/**
 * Helper function to get group at specific path
 */
export function getGroupAtPath(props: {
  card: RuleGroupConditions | undefined
  groupsIdx: number[]
}): GroupConditions | RuleGroupConditions | undefined {
  const { card, groupsIdx } = props
  if (!card || groupsIdx.length === 0) return card

  let current: GroupConditions | RuleGroupConditions | undefined = card
  for (const groupIndex of groupsIdx) {
    current = current.groups?.[groupIndex]
    if (!current) return undefined
  }

  return current
}

/**
 * Helper function to generate field path for groups
 */
export function getGroupFieldPath(props: { ruleIndex: number; groupsIdx: number[]; field: string }): string {
  const { ruleIndex, groupsIdx, field } = props
  if (groupsIdx.length === 0) {
    return `cards[${ruleIndex}].${field}`
  }

  const groupPath = groupsIdx
    .map((idx, level) => {
      return level === 0 ? `groups[${idx}]` : `.groups[${idx}]`
    })
    .join("")

  return `cards[${ruleIndex}].${groupPath}.${field}`
}

/**
 * Helper function to remove group at specific path
 */
export function removeGroupAtPath(props: {
  values: RuleCardFormikValues | TagCardFormikValues
  setFieldValue: (field: string, value: unknown) => void
  ruleIndex: number
  groupsIdx: number[]
}): void {
  const { values, setFieldValue, ruleIndex, groupsIdx } = props
  if (groupsIdx.length === 0) return

  const groupIndex = groupsIdx[groupsIdx.length - 1]
  const parentPath = groupsIdx.slice(0, -1)

  if (parentPath.length === 0) {
    // Remove from root level groups
    setFieldValue(
      `cards[${ruleIndex}].groups`,
      values.cards[ruleIndex]?.groups?.filter((_, idx) => idx !== groupIndex) || [],
    )
  } else {
    // Remove from nested group
    const parentFieldPath = getGroupFieldPath({ ruleIndex, groupsIdx: parentPath, field: "groups" })
    const parentGroup = getGroupAtPath({ card: values.cards[ruleIndex] as RuleGroupConditions, groupsIdx: parentPath })
    setFieldValue(parentFieldPath, parentGroup?.groups?.filter((_, idx) => idx !== groupIndex) || [])
  }
}

/**
 * Remove condition handler for level one conditions
 */
export function removeLevel1ConditionHandler(props: {
  values: RuleCardFormikValues | TagCardFormikValues
  setValues: (values: React.SetStateAction<RuleCardFormikValues | TagCardFormikValues>) => void
  ruleIndex: number
  conditionIndex: number
}): void {
  const { values, setValues, ruleIndex, conditionIndex } = props
  // remove the file from the files array when remove a condition that contains a file
  const removedConditionValue = values.cards[ruleIndex]?.conditions?.[conditionIndex]?.value

  const isFileValue = typeof removedConditionValue === "string" && removedConditionValue.includes("file")
  if (isFileValue) {
    const removedFileId = removedConditionValue?.match(FILE_ID_MATCHER)?.[1]
    setValues((prev: RuleCardFormikValues | TagCardFormikValues) => {
      if (!prev.cards[ruleIndex]?.condition_list_files) return { ...prev }

      prev.cards[ruleIndex].condition_list_files = prev.cards[ruleIndex]?.condition_list_files.filter(
        (file: ConditionListFile) => file.uuid !== removedFileId,
      )
      return { ...prev }
    })
  }
}

/**
 * Handle removal of conditions with file cleanup using path object
 */
export function handleRemoveCondition(props: {
  values: RuleCardFormikValues | TagCardFormikValues
  setFieldValue: (field: string, value: unknown) => void
  setValues: (values: React.SetStateAction<RuleCardFormikValues | TagCardFormikValues>) => void
  levelRemove: (idx: number) => void
  ruleIndex: number
  path: ConditionPath
}): void {
  const { values, setFieldValue, setValues, levelRemove, ruleIndex, path } = props
  const { groupsIdx, conditionIdx } = path

  // Get the condition being removed to check for file values
  const conditionToRemove = getCondition({ values: values as RuleCardFormikValues, ruleIndex, path })
  const removedConditionValue = conditionToRemove?.value

  // Handle file cleanup
  const isFileValue = typeof removedConditionValue === "string" && removedConditionValue.includes("file")
  if (isFileValue) {
    const removedFileId = removedConditionValue?.match(FILE_ID_MATCHER)?.[1]
    setValues((prev: RuleCardFormikValues | TagCardFormikValues) => {
      if (!prev.cards[ruleIndex]?.condition_list_files) return { ...prev }

      prev.cards[ruleIndex].condition_list_files = prev.cards[ruleIndex]?.condition_list_files.filter(
        (file: ConditionListFile) => file.uuid !== removedFileId,
      ) as ConditionListFile[]

      return { ...prev }
    })
  }

  // Handle removal logic
  if (groupsIdx.length === 0) {
    // Root level condition - use existing logic
    return removeLevel1ConditionHandler({ values, setValues, ruleIndex, conditionIndex: conditionIdx })
  }

  // For nested conditions, handle group consolidation
  // Get the current group containing the condition
  const currentGroup = getGroupAtPath({ card: values.cards[ruleIndex] as RuleGroupConditions, groupsIdx })
  const currentConditionsLength = currentGroup?.conditions?.length || 0

  if (currentConditionsLength <= 2) {
    // If only 2 or fewer conditions, remove the entire group and migrate remaining condition
    const conditionToMigrate = conditionIdx === 0 ? currentGroup?.conditions?.[1] : currentGroup?.conditions?.[0]

    if (conditionToMigrate) {
      // Migrate to parent level
      const parentPath = groupsIdx.slice(0, -1)
      if (parentPath.length === 0) {
        // Migrate to root level
        setFieldValue(`cards[${ruleIndex}].conditions`, [
          ...(values.cards[ruleIndex]?.conditions || []),
          conditionToMigrate,
        ])
      } else {
        // Migrate to parent group
        const parentFieldPath = getGroupFieldPath({ ruleIndex, groupsIdx: parentPath, field: "conditions" })
        const parentGroup = getGroupAtPath({
          card: values.cards[ruleIndex] as RuleGroupConditions,
          groupsIdx: parentPath,
        })
        setFieldValue(parentFieldPath, [...(parentGroup?.conditions || []), conditionToMigrate])
      }
    }

    // Remove the entire group
    removeGroupAtPath({ values, setFieldValue, ruleIndex, groupsIdx })

    // If this group had nested groups, migrate them to parent level
    if (currentGroup?.groups && currentGroup.groups.length > 0) {
      const parentPath = groupsIdx.slice(0, -1)
      const parentGroupsPath =
        parentPath.length === 0
          ? `cards[${ruleIndex}].groups`
          : getGroupFieldPath({ ruleIndex, groupsIdx: parentPath, field: "groups" })

      const parentGroup =
        parentPath.length === 0
          ? values.cards[ruleIndex]
          : getGroupAtPath({ card: values.cards[ruleIndex] as RuleGroupConditions, groupsIdx: parentPath })

      setFieldValue(parentGroupsPath, [...(parentGroup?.groups || []), ...currentGroup.groups])
    }
  } else {
    // Just remove the specific condition
    levelRemove(conditionIdx)
  }
}

/**
 * Handle adding new label value
 */
export async function handleAddNewValue(props: {
  values: RuleCardFormikValues
  setFieldValue: (field: string, value: unknown) => void
  index: number
  projectId: string
  createLabelMutation: UseMutationResult<
    AxiosResponse<Label>,
    AxiosError,
    {
      project_uuid: string
      name: string
    },
    unknown
  >
}): Promise<void> {
  const { values, setFieldValue, index, projectId, createLabelMutation } = props
  const labelValue = values.cards[index]?.label

  if (labelValue && isInputValueValid("label", labelValue, PROHIBITED_LABELS_KEYWORDS)) {
    await createLabelMutation
      .mutateAsync({
        project_uuid: projectId,
        name: values.cards[index]?.label ?? "",
      })
      .then((res: AxiosResponse<Label>) => {
        setFieldValue(`cards[${index}].label_id`, res.data.uuid)
      })
  } else {
    setFieldValue(`cards[${index}].label`, values.cards[index]?.label)
  }
}

/**
 * Update condition value using path object
 */
export function updateConditionValue(props: {
  values: RuleCardFormikValues
  ruleIndex: number
  path: ConditionPath
  newValue: string
}): RuleCardFormikValues {
  const { values, ruleIndex, path, newValue } = props
  const updatedValues = { ...values }
  const card = updatedValues.cards?.[ruleIndex]
  if (!card) return updatedValues

  const { groupsIdx, conditionIdx } = path

  // Base case: if no groups, it's a root level condition
  if (groupsIdx.length === 0) {
    if (card.conditions?.[conditionIdx]) {
      card.conditions[conditionIdx].value = newValue
    }
    return updatedValues
  }

  // Navigate through groups
  let currentGroup: GroupConditions | RuleGroupConditions | undefined = card
  for (const groupIndex of groupsIdx) {
    currentGroup = currentGroup?.groups?.[groupIndex]
    if (!currentGroup) return updatedValues
  }

  // Update the condition in the final group
  if (currentGroup.conditions?.[conditionIdx]) {
    currentGroup.conditions[conditionIdx].value = newValue
  }

  return updatedValues
}

/**
 * Check if a condition can be removed based on its position in the nested structure
 */
export function canRemoveCondition(props: {
  values: RuleCardFormikValues
  ruleIndex: number
  path: ConditionPath
}): boolean {
  const { values, ruleIndex, path } = props
  const card = values?.cards?.[ruleIndex]
  if (!card) return false

  const { groupsIdx, conditionIdx } = path

  // Base case: if no groups, it's a root level condition
  if (groupsIdx.length === 0) {
    // Can remove if it's not the first condition or if there are multiple conditions
    return conditionIdx !== 0 || (card.conditions?.length || 0) > 1
  }

  // For nested conditions, they can always be removed
  return true
}

/**
 * Create empty condition object
 */
export function createEmptyCondition(): Rule {
  return {
    id: uuidv4(),
    feature: "",
    andOr: "and",
    operator: Operators["="],
    valueOrFeature: "Value",
    type: "string",
    value: "",
    secondValue: "",
    schemaFeatureType: WorkflowInputFeature.type.TEXT,
  }
}

/**
 * Get the field path for a group's andOr property
 */
export const getGroupAndOrFieldPath = (props: {
  ruleIndex: number
  currentPath: number[]
  groupIndex: number
}): string => {
  const { ruleIndex, currentPath, groupIndex } = props
  if (currentPath.length === 1) {
    return `cards[${ruleIndex}].groups[${groupIndex}].andOr`
  }

  return (
    currentPath
      .slice(0, -1)
      .reduce((acc, idx, i) => (i === 0 ? `cards[${ruleIndex}].groups[${idx}]` : `${acc}.groups[${idx}]`), "") +
    `.groups[${groupIndex}].andOr`
  )
}

/**
 * Get the field path for a group's conditions array
 */
export const getFieldArrayName = (props: { ruleIndex: number; currentPath: number[] }): string => {
  const { ruleIndex, currentPath } = props
  return `cards[${ruleIndex}].${currentPath
    .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
    .join("")}.conditions`
}

/**
 * Get the field path for a specific condition within a nested group
 */
export const getConditionId = (props: { ruleIndex: number; currentPath: number[]; conditionIndex: number }): string => {
  const { ruleIndex, currentPath, conditionIndex } = props
  return `cards[${ruleIndex}].${currentPath
    .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
    .join("")}.conditions[${conditionIndex}]`
}

/**
 * Get the field path for a condition's andOr property within a nested group
 */
export const getConditionAndOrFieldPath = (props: {
  ruleIndex: number
  currentPath: number[]
  conditionIndex: number
}): string => {
  const { ruleIndex, currentPath, conditionIndex } = props
  return `cards[${ruleIndex}].${currentPath
    .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
    .join("")}.conditions[${conditionIndex}].andOr`
}

/**
 * Get the field path for adding a condition to a nested group's conditions array
 */
export const getAddConditionFieldPath = (props: { ruleIndex: number; currentPath: number[] }): string => {
  const { ruleIndex, currentPath } = props
  if (currentPath.length === 0) {
    return `cards[${ruleIndex}].conditions`
  }
  return `cards[${ruleIndex}].${currentPath
    .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
    .join("")}.conditions`
}

/**
 * Get the field path for adding a group to a nested group's groups array
 */
export const getAddGroupFieldPath = (props: { ruleIndex: number; currentPath: number[] }): string => {
  const { ruleIndex, currentPath } = props
  if (currentPath.length === 0) {
    return `cards[${ruleIndex}].groups`
  }
  return `cards[${ruleIndex}].${currentPath
    .map((idx, i) => (i === 0 ? `groups[${idx}]` : `.groups[${idx}]`))
    .join("")}.groups`
}
